import { useState, useEffect } from "react";
import { Loader2, Plus, X, Ban, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const WEEKDAYS = [
  { i: 0, l: "Lunes" },
  { i: 1, l: "Martes" },
  { i: 2, l: "Miércoles" },
  { i: 3, l: "Jueves" },
  { i: 4, l: "Viernes" },
  { i: 5, l: "Sábado" },
  { i: 6, l: "Domingo" },
];

export default function AvailabilityTab() {
  const [loading, setLoading] = useState(true);
  const [workingDays, setWorkingDays] = useState([]);
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [saving, setSaving] = useState(false);
  const [blockDate, setBlockDate] = useState("");
  const [blockTime, setBlockTime] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/availability");
      setWorkingDays(data.working_days);
      setSlots(data.time_slots);
      setBlocked(data.blocked);
    } catch {
      toast.error("Error al cargar disponibilidad.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleDay = (i) => setWorkingDays((d) => (d.includes(i) ? d.filter((x) => x !== i) : [...d, i].sort()));
  const addSlot = () => {
    if (!/^\d{2}:\d{2}$/.test(newSlot)) {
      toast.error("Formato de hora inválido (HH:MM).");
      return;
    }
    if (slots.includes(newSlot)) return;
    setSlots((s) => [...s, newSlot].sort());
    setNewSlot("");
  };
  const removeSlot = (t) => setSlots((s) => s.filter((x) => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/availability", { working_days: workingDays, time_slots: slots });
      toast.success("Disponibilidad guardada.");
    } catch {
      toast.error("No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  const block = async () => {
    if (!blockDate || !blockTime) return;
    try {
      await api.post("/admin/availability/block", { date: blockDate, time: blockTime });
      toast.success("Hora bloqueada.");
      setBlockDate("");
      setBlockTime("");
      load();
    } catch {
      toast.error("No se pudo bloquear.");
    }
  };

  const unblock = async (b) => {
    try {
      await api.post("/admin/availability/unblock", { date: b.date, time: b.time });
      load();
    } catch {
      toast.error("No se pudo desbloquear.");
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/40 py-16 justify-center"><Loader2 className="animate-spin" size={16} /> Cargando...</div>;

  return (
    <div data-testid="availability-tab" className="grid lg:grid-cols-2 gap-6">
      {/* Config */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6">
        <h3 className="font-display font-bold uppercase text-base mb-5">Días y horarios disponibles</h3>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-3">Días de la semana</p>
        <div className="flex flex-wrap gap-2 mb-6">
          {WEEKDAYS.map((d) => (
            <button
              key={d.i}
              data-testid={`weekday-${d.i}`}
              onClick={() => toggleDay(d.i)}
              className={`px-3 py-2 rounded-sm border text-xs font-display uppercase ${workingDays.includes(d.i) ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/15 text-white/50"}`}
            >
              {d.l}
            </button>
          ))}
        </div>

        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-3">Franjas horarias</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {slots.map((t) => (
            <span key={t} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-white/15 text-xs text-white">
              {t}
              <button data-testid={`remove-slot-${t}`} onClick={() => removeSlot(t)} className="text-white/40 hover:text-red-400"><X size={12} /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2 mb-6">
          <input data-testid="new-slot-input" type="time" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} className="bg-black border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
          <button data-testid="add-slot" onClick={addSlot} className="btn-outline-gold rounded-sm px-3 py-2 text-xs inline-flex items-center gap-1"><Plus size={14} /> Añadir</button>
        </div>

        <button data-testid="save-availability" onClick={save} disabled={saving} className="btn-gold rounded-sm w-full py-3 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={15} /> Guardar configuración</>}
        </button>
      </div>

      {/* Block specific slots */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-sm p-6">
        <h3 className="font-display font-bold uppercase text-base mb-5">Bloquear horas concretas</h3>
        <div className="flex flex-col sm:flex-row gap-2 mb-5">
          <input data-testid="block-date" type="date" value={blockDate} onChange={(e) => setBlockDate(e.target.value)} className="bg-black border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none flex-1" />
          <input data-testid="block-time" type="time" value={blockTime} onChange={(e) => setBlockTime(e.target.value)} className="bg-black border border-white/15 rounded-sm px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none" />
          <button data-testid="block-submit" onClick={block} className="btn-outline-gold rounded-sm px-3 py-2 text-xs inline-flex items-center gap-1 justify-center"><Ban size={14} /> Bloquear</button>
        </div>
        <p className="text-[11px] uppercase tracking-wide text-white/40 mb-3">Horas bloqueadas</p>
        {blocked.length === 0 ? (
          <p className="text-white/30 text-sm">No hay horas bloqueadas.</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {blocked.map((b) => (
              <div key={`${b.date}-${b.time}`} className="flex items-center justify-between border border-white/10 rounded-sm px-3 py-2 text-sm">
                <span className="text-white/80">{b.date} · <span className="text-[#D4AF37]">{b.time}</span></span>
                <button data-testid={`unblock-${b.date}-${b.time}`} onClick={() => unblock(b)} className="text-white/40 hover:text-[#D4AF37] text-xs">Desbloquear</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
